import React, { useState, useEffect } from 'react';
import { FaEnvelope, FaCalendarAlt, FaBuilding, FaInfoCircle, FaCheck, FaClock, FaFilter, FaSearch, FaChevronDown } from 'react-icons/fa';

interface ContactRequest {
  _id: string;
  name: string;
  email: string;
  institution: string;
  type: 'demo' | 'soporte' | 'evolucion';
  message: string;
  status: 'pendiente' | 'contactado' | 'resuelto';
  createdAt: string;
}

const ContactRequestsPanel: React.FC = () => {
  const [contacts, setContacts] = useState<ContactRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pendiente' | 'contactado' | 'resuelto'>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'demo' | 'soporte' | 'evolucion'>('all');
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const API_URL = window.location.hostname === 'localhost' ? 'http://localhost:5000' : '';
      const token = localStorage.getItem('token');
      
      console.log(`📡 Obteniendo contactos desde: ${API_URL}/api/system-admin/contacts`);
      const response = await fetch(`${API_URL}/api/system-admin/contacts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error("❌ Respuesta no es JSON:", text.substring(0, 100));
        return;
      }

      const data = await response.json();
      if (data.success) {
        setContacts(data.contacts);
      } else {
        console.error("❌ Error al obtener contactos:", data.message);
      }
    } catch (error) {
      console.error('❌ Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, newStatus: string) => {
    try {
      setUpdatingId(id);
      const API_URL = window.location.hostname === 'localhost' ? 'http://localhost:5000' : '';
      const token = localStorage.getItem('token');
      
      console.log(`📡 Actualizando contacto ${id} a ${newStatus}`);
      const response = await fetch(`${API_URL}/api/system-admin/contacts/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error("❌ Respuesta no es JSON:", text.substring(0, 100));
        return;
      }

      const data = await response.json();
      if (data.success) {
        setContacts(contacts.map(c => c._id === id ? { ...c, status: newStatus as any } : c));
      } else {
        console.error("❌ Error al actualizar estado:", data.message);
      }
    } catch (error) {
      console.error('❌ Error updating contact status:', error);
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredContacts = contacts.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.institution.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    const matchesType = typeFilter === 'all' || c.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pendiente': return 'bg-yellow-100 text-yellow-800';
      case 'contactado': return 'bg-blue-100 text-blue-800';
      case 'resuelto': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'demo': return 'Solicitud Demo';
      case 'soporte': return 'Soporte';
      case 'evolucion': return 'Evolución';
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Solicitudes de Contacto</h2>
            <p className="text-sm text-gray-500">Gestiona leads y requerimientos desde la landing page</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input 
                type="text" 
                placeholder="Buscar..."
                className="pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            
            <select 
              className="border border-gray-200 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as any)}
            >
              <option value="all">Todos los estados</option>
              <option value="pendiente">Pendientes</option>
              <option value="contactado">Contactados</option>
              <option value="resuelto">Resueltos</option>
            </select>

            <select 
              className="border border-gray-200 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value as any)}
            >
              <option value="all">Todos los tipos</option>
              <option value="demo">Demos</option>
              <option value="soporte">Soporte</option>
              <option value="evolucion">Evolución</option>
            </select>
          </div>
        </div>
      </div>

      {/* Grid of Contacts */}
      <div className="grid grid-cols-1 gap-4">
        {filteredContacts.length === 0 ? (
          <div className="bg-white rounded-2xl p-20 text-center border border-dashed border-gray-200">
            <FaEnvelope className="mx-auto text-gray-300 text-5xl mb-4" />
            <p className="text-gray-500 font-medium">No se encontraron solicitudes</p>
          </div>
        ) : (
          filteredContacts.map(contact => (
            <div key={contact._id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
                  <div className="flex-1 space-y-4">
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusColor(contact.status)}`}>
                        {contact.status}
                      </span>
                      <span className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-[10px] font-bold uppercase tracking-wider">
                        {getTypeLabel(contact.type)}
                      </span>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{contact.name}</h3>
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <FaEnvelope className="text-indigo-400" />
                          {contact.email}
                        </div>
                        <div className="flex items-center gap-2">
                          <FaBuilding className="text-indigo-400" />
                          {contact.institution}
                        </div>
                        <div className="flex items-center gap-2">
                          <FaCalendarAlt className="text-indigo-400" />
                          {new Date(contact.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>

                    {contact.message && (
                      <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600 border border-gray-100">
                        <p className="font-bold text-gray-400 text-[10px] uppercase mb-2 tracking-widest flex items-center gap-2">
                          <FaInfoCircle /> Mensaje del solicitante
                        </p>
                        {contact.message}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 min-w-[160px]">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">Acciones</p>
                    <button 
                      onClick={() => updateStatus(contact._id, 'contactado')}
                      disabled={updatingId === contact._id || contact.status === 'contactado'}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-xl text-xs font-bold hover:bg-blue-100 transition-colors disabled:opacity-50"
                    >
                      <FaClock /> Marcar Contactado
                    </button>
                    <button 
                      onClick={() => updateStatus(contact._id, 'resuelto')}
                      disabled={updatingId === contact._id || contact.status === 'resuelto'}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-xl text-xs font-bold hover:bg-green-100 transition-colors disabled:opacity-50"
                    >
                      <FaCheck /> Marcar Resuelto
                    </button>
                    <button 
                      onClick={() => updateStatus(contact._id, 'pendiente')}
                      disabled={updatingId === contact._id || contact.status === 'pendiente'}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-yellow-50 text-yellow-700 rounded-xl text-xs font-bold hover:bg-yellow-100 transition-colors disabled:opacity-50"
                    >
                      <FaChevronDown /> Volver a Pendiente
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ContactRequestsPanel;
